import { useState } from "react";
import { Loader2, Package } from "lucide-react";
import axios from "axios";
import FlipCounter from "./FlipCounter";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "./ui/accordion";

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

export default function LockScreen({ onLoginSuccess, stats }) {
  const [firstName, setFirstName] = useState("");
  const [email, setEmail] = useState("");
  const [shopName, setShopName] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!firstName.trim() || !shopName.trim() || !password.trim()) {
      setError("Please fill in your name, shop name and password");
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(`${API}/login`, {
        firstName: firstName.trim(),
        email: email.trim(),
        password: password.trim()
      });

      if (response.data.success) {
        onLoginSuccess(response.data.firstName, shopName.trim());
      }
    } catch (err) {
      if (err.response?.status === 401) {
        setError("Incorrect password");
      } else {
        setError("Login failed. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] noise-texture flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-12">
          <div className="flex items-end justify-center gap-2 mb-6">
            <h1
              className="font-wordmark uppercase bg-clip-text text-transparent leading-[0.95] select-none"
              style={{
                fontSize: 'clamp(5rem, 14vw, 9rem)',
                fontWeight: 500,
                letterSpacing: '0.02em',
                backgroundImage: 'linear-gradient(to right, #2DD4FF 0%, #5B8BFF 35%, #9461FF 70%, #D63DEC 100%)',
              }}
              data-testid="surfify-wordmark"
            >
              SURFIFY
            </h1>
            <Package
              className="text-[#D63DEC] mb-[0.18em] shrink-0"
              strokeWidth={2}
              style={{ width: 'clamp(1rem, 2.5vw, 1.5rem)', height: 'clamp(1rem, 2.5vw, 1.5rem)' }}
              data-testid="surfify-wordmark-dot"
            />
          </div>
          {stats && (stats.descriptions_generated > 0 || stats.images_downloaded > 0) && (
            <div className="text-center space-y-2" data-testid="lockscreen-stats">
              <p className="text-zinc-500 text-xs font-mono">
                You legends have saved time with:
              </p>
              <p className="text-zinc-300 text-sm font-mono leading-relaxed">
                <FlipCounter target={stats.descriptions_generated} delay={600} />
                <span className="text-zinc-500 mx-1">product descriptions generated and</span>
                <FlipCounter target={stats.images_downloaded} delay={900} />
                <span className="text-zinc-500 mx-1">images downloaded.</span>
              </p>
            </div>
          )}

          {/* The Value Stack */}
          <div className="mt-6 max-w-md mx-auto text-left">
            <Accordion type="single" collapsible className="border border-[#27272A] bg-[#0A0A0A]/60">
              <AccordionItem value="value-stack" className="border-b-0">
                <AccordionTrigger
                  className="px-4 py-3 text-xs font-mono uppercase tracking-wider text-zinc-300 hover:no-underline hover:text-white"
                  data-testid="value-stack-trigger"
                >
                  The Value Stack
                </AccordionTrigger>
                <AccordionContent className="px-4 pb-5 pt-1" data-testid="value-stack-content">
                  <div className="space-y-4 text-xs font-mono leading-relaxed">
                    <div>
                      <p className="text-[#00FFFF] uppercase tracking-wider mb-1">Save Time</p>
                      <p className="text-zinc-300 font-bold">
                        Re-writing, tweaking and customising product descriptions for every new arrival takes time as does downloading images. Surfify does it in a few clicks.
                      </p>
                    </div>
                    <div>
                      <p className="text-[#00FFFF] uppercase tracking-wider mb-1">Discoverable 24/7</p>
                      <p className="text-zinc-300 font-bold">
                        Rich descriptions and smart file names means search engines, AI bots and Google Shopping are getting the right signals, around the clock.
                      </p>
                    </div>
                    <div>
                      <p className="text-[#00FFFF] uppercase tracking-wider mb-1">Customer Experience</p>
                      <p className="text-zinc-300 font-bold">
                        We've all landed on a product page with one image and three bullet points. Feels like no-one's home. Customers will see and feel the difference.
                      </p>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
          {/* no emoji */}
        </div>

        {/* Login Form */}
        <div className="bg-[#0A0A0A] border border-[#27272A] p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* First Name */}
            <div>
              <label className="block text-xs font-mono uppercase tracking-wider text-zinc-400 mb-3">
                FIRST NAME
              </label>
              <input
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="Enter your first name"
                className="w-full bg-transparent border-b-2 border-[#333333] text-lg text-white placeholder:text-zinc-700 focus:border-[#00FFFF] focus:outline-none h-12 px-0 font-mono transition-all"
                disabled={loading}
                data-testid="firstname-input"
              />
            </div>

            {/* Email */}
            <div>
              <label className="block text-xs font-mono uppercase tracking-wider text-zinc-400 mb-3">
                EMAIL
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                className="w-full bg-transparent border-b-2 border-[#333333] text-lg text-white placeholder:text-zinc-700 focus:border-[#00FFFF] focus:outline-none h-12 px-0 font-mono transition-all"
                disabled={loading}
                data-testid="email-input"
              />
            </div>

            {/* Shop Name */}
            <div>
              <label className="block text-xs font-mono uppercase tracking-wider text-zinc-400 mb-3">
                SHOP NAME
              </label>
              <input
                type="text"
                value={shopName}
                onChange={(e) => setShopName(e.target.value)}
                placeholder="your shop's name"
                className="w-full bg-transparent border-b-2 border-[#333333] text-lg text-white placeholder:text-zinc-700 focus:border-[#00FFFF] focus:outline-none h-12 px-0 font-mono transition-all"
                disabled={loading}
                data-testid="shopname-input"
              />
            </div>

            {/* Password */}
            <div>
              <label className="block text-xs font-mono uppercase tracking-wider text-zinc-400 mb-3">
                ACCESS CODE
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter access code"
                className="w-full bg-transparent border-b-2 border-[#333333] text-lg text-white placeholder:text-zinc-700 focus:border-[#00FFFF] focus:outline-none h-12 px-0 font-mono transition-all"
                disabled={loading}
                data-testid="password-input"
              />
            </div>

            {/* Error */}
            {error && (
              <div className="text-sm text-red-500 font-mono" data-testid="login-error">
                {error}
              </div>
            )}

            {/* Submit */}
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-[#00FFFF] text-black hover:bg-[#00CCDD] font-bold uppercase tracking-wider px-8 py-3 transition-all hover:shadow-[0_0_20px_rgba(0,255,255,0.4)] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              data-testid="login-submit-btn"
            >
              {loading ? (
                <><Loader2 className="w-5 h-5 animate-spin" />CHECKING...</>
              ) : (
                <>ENTER SURFIFY</>
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
